import { FC } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import DOMPurify from 'dompurify'

import { providersCasesEmail } from '@fireflyhealth/api'

import { RichTextEditor } from '../RichTextEditor/RichtextEditor'
import { ALLOWED_ATTR, ALLOWED_TAGS } from '../RichTextEditor/utils'
import { listCaseEmailKey } from '~/entities/case'
import { useMutation } from '~/shared/hooks/query'
import { listPersonTodoKey } from '~/shared/lib/queryKeys'
import { FileWithType } from './EmailFileUpload'

interface EmailEditorProps {
  content: string
  case_id: number
  onCancel: () => void
  submitLabel?: string
}
const useSendCaseEmail = (caseId: number) => {
  const client = useQueryClient()

  return useMutation(
    (formData: FormData) =>
      providersCasesEmail.create({ id: caseId, provider_id: 'me' }, { data: formData }),
    {
      onSuccess: async () => {
        await client.invalidateQueries({ queryKey: [listCaseEmailKey] })
        await client.invalidateQueries({ queryKey: [listPersonTodoKey] })
      },
    },
    { success: 'Email sent!', error: 'Email could not be sent' },
  )
}

export const EmailEditor: FC<EmailEditorProps> = props => {
  const { mutate: sendCaseEmail, isPending: isLoadingSendCaseEmail } = useSendCaseEmail(
    props.case_id,
  )
  const onSubmit = (content: string, files?: FileWithType[]) => {
    const santisedContent = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ALLOWED_TAGS,
      ALLOWED_ATTR: ALLOWED_ATTR,
    })
    const formData = new FormData()
    formData.append('message', santisedContent)
    files?.forEach(file => {
      if (file?.data) formData.append('attachments', file.data)
    })
    sendCaseEmail(formData)
  }

  return (
    <RichTextEditor
      content={props.content}
      onSubmit={onSubmit}
      isLoading={isLoadingSendCaseEmail}
      onCancel={props.onCancel}
      submitLabel={props.submitLabel}
    />
  )
}
